import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Attachment, Claim } from '@dataTypes/claimFeature.types'
import { fileFromUrl, getFileIcon } from '@utils/file.utils'
import { formatDate } from '@utils/date.utils'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import { attachmentTypeOptions } from '@utils/claims.util'
import FileViewer from '../FileViewerModal/FileViewer.component'
import DataTreeGridComponent from '@components/common/DataTreeGridComponent/DataTreeGridComponent'
import { RowDataBoundEventArgs } from '@syncfusion/ej2-react-grids'
import { TreeGridComponent } from '@syncfusion/ej2-react-treegrid'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'

interface ClaimMemoAttachmentsProps {
  claim: Claim
}

const SmartText = ({
  text,
  tooltipContent,
}: {
  text: string
  tooltipContent: string
}): JSX.Element => {
  const ref = useRef<HTMLSpanElement | null>(null)
  const [applyEllipsis, setApplyEllipsis] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    requestAnimationFrame(() => {
      const naturalHeight = el.scrollHeight

      el.style.whiteSpace = 'nowrap'
      el.style.textOverflow = 'ellipsis'
      el.style.overflow = 'hidden'
      el.style.maxWidth = '96%'

      const ellipsisHeight = el.scrollHeight

      if (naturalHeight > ellipsisHeight) {
        setApplyEllipsis(true)
      } else {
        el.style.whiteSpace = ''
        el.style.textOverflow = ''
        el.style.overflow = ''
        el.style.maxWidth = ''
      }
    })
  }, [text])

  const spanElement = (
    <span
      ref={ref}
      style={{
        fontSize: '0.875rem',
        fontWeight: '600',
        color: 'var(--blue-600)',
        marginLeft: '10px',
        whiteSpace: applyEllipsis ? 'nowrap' : undefined,
        textOverflow: applyEllipsis ? 'ellipsis' : undefined,
        maxWidth: applyEllipsis ? '96%' : undefined,
        overflow: applyEllipsis ? 'hidden' : undefined,
      }}
    >
      {text}
    </span>
  )

  return applyEllipsis ? (
    <TooltipComponent
      content={tooltipContent}
      opensOn="Hover"
      cssClass="e-tooltip-template-css e-tooltip-tab-feature"
      position="TopCenter"
    >
      {spanElement}
    </TooltipComponent>
  ) : (
    <>{spanElement}</>
  )
}

const ClaimMemoAttachments: React.FC<ClaimMemoAttachmentsProps> = ({
  claim,
}) => {
  const treeGridRef = useRef<TreeGridComponent | null>(null)
  const [fileViewerInfo, setFileViewerInfo] = useState<{
    title: string
    show: boolean
    file: File
    url: string
  }>({
    title: '',
    show: false,
    file: {} as File,
    url: '',
  })
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const handleViewAttachment = async (
    attachment: Attachment,
  ): Promise<void> => {
    showLoading({
      container: '.claims-memo-attachments',
    })
    const file = await fileFromUrl(
      attachment.fileUrl!,
      attachment.fileName!,
      attachment.mimeType,
    )
    hideLoading('.claims-memo-attachments')
    setFileViewerInfo({
      title: `Viewing: ${attachment.item}`,
      show: true,
      file: file,
      url: attachment.fileUrl!,
    })
  }

  const attachmentColumns = useMemo(
    () => [
      {
        field: 'itemNo',
        headerText: 'Item #',
        width: '70px',
        template: (rowData: any) => {
          if (!rowData.itemNo)
            return (
              <SmartText
                text={`${rowData.id} ${rowData.id === 'General' ? '' : `(${rowData.description})`}`}
                tooltipContent={`${rowData.id} ${rowData.id === 'General' ? '' : `(${rowData.description})`}`}
              />
            )
          return <span>{rowData.itemNo}</span>
        },
      },
      {
        field: 'item',
        headerText: 'Item',
        width: '70px',
        template: (rowData: any) => {
          if (!rowData.item) return <></>
          return (
            <div
              className="align-items-center justify-content-center"
              title={rowData.item}
            >
              <i className={`${getFileIcon(rowData.item)} file-icon fs-1`} />
            </div>
          )
        },
      },
      {
        field: 'formatType',
        headerText: 'Format type',
        width: '100px',
        template: (rowData: Attachment) => {
          if (!rowData.formatType) return <></>
          return <span>{rowData.formatType.toString().toUpperCase()}</span>
        },
      },
      {
        field: 'attachmentType',
        headerText: 'Attachment type',
        width: '200px',
        template: (rowData: Attachment): JSX.Element => {
          if (!rowData.attachmentType) return <></>
          return (
            <span>
              {attachmentTypeOptions.find(
                (option) => option.value === rowData.attachmentType,
              )?.label || 'N/A'}
            </span>
          )
        },
      },
      {
        field: 'dateTaken',
        headerText: 'Date taken',
        width: '80px',
        template: (rowData: any) => {
          if (!rowData.dateTaken) return <></>
          return <span>{formatDate(rowData.dateTaken)}</span>
        },
      },
      {
        field: 'actions',
        headerText: 'Actions',
        width: '70px',
        template: (rowData: any) => {
          if (!rowData.item) return <></>
          return (
            <ActionButtons
              onView={() => handleViewAttachment(rowData)}
              showMoreOptions={false}
            />
          )
        },
      },
    ],
    [],
  )

  const rowDataBound = (args: RowDataBoundEventArgs): void => {
    const rowData = args.data as any
    const row = args.row as HTMLTableRowElement

    if (!rowData.itemNo) {
      const tds = Array.from(row.children) as HTMLTableCellElement[]

      if (tds.length > 1) {
        const firstTd = tds[0]
        const rest = tds.slice(1)
        rest.forEach((td) => td.remove())
        firstTd.colSpan = tds.length
        firstTd.setAttribute(
          'style',
          'text-align: left !important; padding-left: 10px !important;',
        )
      }

      args.row?.classList.add('procedure-group-row')
      row.style.cursor = 'pointer'

      row.onclick = () => {
        const isExpanded = row.getAttribute('aria-expanded') === 'true'
        if (isExpanded) {
          treeGridRef.current?.collapseRow(row)
        } else {
          treeGridRef.current?.expandRow(row)
        }
      }
    }
  }

  const allAttachmentsElement = useMemo(() => {
    const parentData: any[] = []
    const allAttachments = [
      ...claim.attachments.map((attachment) => {
        if (!parentData.find((parent) => parent.id === 'General')) {
          parentData.push({
            id: 'General',
            parentId: null,
          })
        }
        return {
          ...attachment,
          parentId: 'General',
        }
      }),
      ...claim.procedures.flatMap((procedure) => {
        return procedure.procedureAttachments.map((attachment) => {
          if (
            !parentData.find((parent) => parent.id === procedure.procedureCode)
          ) {
            parentData.push({
              id: procedure.procedureCode,
              description: procedure.procedureName,
              parentId: null,
            })
          }
          return {
            ...attachment,
            parentId: procedure.procedureCode,
          }
        })
      }),
    ]
    allAttachments.push(...parentData)
    if (allAttachments.length === 0) return null
    return (
      <DataTreeGridComponent
        ref={treeGridRef}
        data={allAttachments}
        columns={attachmentColumns}
        idMapping="id"
        parentIdMapping="parentId"
        treeColumnIndex={0}
        rowHeight={36}
        allowPaging={true}
        allowTextWrap={true}
        pageSize={10}
        hasFooter={false}
        customCSS="claim-attachment-table-review"
        rowDataBoundExternal={rowDataBound}
      />
    )
  }, [attachmentColumns, claim.attachments, claim.procedures])

  return (
    <>
      {allAttachmentsElement}
      {fileViewerInfo.show && (
        <FileViewer
          show={fileViewerInfo.show}
          onHide={() => setFileViewerInfo({ ...fileViewerInfo, show: false })}
          title={fileViewerInfo.title}
          {...(fileViewerInfo.file && { sourcefile: fileViewerInfo.file })}
          {...(fileViewerInfo.url && { sourceUrl: fileViewerInfo.url })}
        />
      )}
    </>
  )
}

export default ClaimMemoAttachments
